import type { TerminalEvent, TerminalSubjectKind } from '@developerz/domain';
import { createMemo, type JSX, Show } from 'solid-js';
import { t } from '../../i18n';
import {
  atScrollbackCeiling,
  type BufferRow,
  compareRow,
  TERMINAL_BUFFER_CAPACITY,
} from '../../lib/terminal/buffer';
import {
  filterTerminalRows,
  filterVerboseRows,
  isEmptyTerminalFilter,
  parseTerminalFilter,
} from '../../lib/terminal/filter';
import { usePaneSearch } from '../../lib/terminal/use-pane-search';
import type { TerminalTimeMode } from '../../lib/terminal/use-terminal-panes';
import { absolute } from '../../lib/time';
import { TerminalLog } from './TerminalLog';
import { TerminalPaneSearch } from './TerminalPaneSearch';
import { TerminalPaneTitlebar } from './TerminalPaneTitlebar';

// One pane's own body (#4397, epic #4391): title bar + filter box + the log
// window (`TerminalLog`, reused unchanged — the ring buffer, the follow
// mode, the loading/error/denied states all stay exactly what #4395/#4396
// built). CONTAINER-ish presenter: it owns the pane's OWN local filter-text
// input state, but every row it renders is a PROP — `rows` is the one shared
// buffer `Terminal.tsx` merges the backfill, the live tail and the system
// lines into. No pane calls `useTerminalStream`/`useTerminalEvents` itself
// (`lib/sse-one-seam.test.ts` pins the single live caller in `Terminal.tsx`).
//
// THE SCROLL-BACK CEILING (issue step 3 / D-N; plan 101 slice 19 row 19-2).
// `rows` is the shared ring buffer, capped at `TERMINAL_BUFFER_CAPACITY` and
// keeping the NEWEST rows: once it is full, a scroll-back page (older than
// every row held) is evicted by the very merge that brought it in. So at that
// point (`atScrollbackCeiling`) the pane stops offering "Load earlier lines"
// and states where its scroll-back ends instead. Every pane shares the one
// buffer, so the ceiling is the same in every pane, filtered or not.
export function TerminalPane(props: {
  readonly id: string;
  /**
   * ACCESSORS, not values — see `Terminal.tsx`'s `renderPane` docblock. Read
   * inside this component's own `createMemo`s below, matching the shape the
   * caller passes them in.
   */
  readonly label: () => string;
  readonly rows: () => readonly TerminalEvent[];
  readonly filterText: () => string;
  readonly onFilterTextChange: (text: string) => void;
  /**
   * The status line's click-to-filter narrowing (`subject.kind`), owned by
   * `Terminal.tsx` and targeted at whichever pane is FOCUSED — a separate
   * axis from the `key=value` grammar above (advisor: keep it a per-pane
   * field applied in series, not a `kind=` token the issue never asked the
   * grammar to parse). Composed as an extra AND clause, same as every other
   * filter field.
   */
  readonly kindFilter: () => TerminalSubjectKind | null;
  readonly focused: () => boolean;
  readonly live: () => boolean;
  readonly zoomed: () => boolean;
  /** #4399 slice 08 — this pane's own verbose toggle, over the SAME shared
   *  buffer every pane reads (see `filterVerboseRows` below). */
  readonly verbose: () => boolean;
  /** #4398 — this pane's follow mode, lifted into `use-terminal-panes.ts` so the command line reaches it. */
  readonly follow: () => boolean;
  readonly onFollowChange: (on: boolean) => void;
  /** #4398 `clear` — rows at or before this marker are hidden from THIS pane only. */
  readonly clearedAt: () => BufferRow | null;
  /** #4401 — the window-wide timestamp style, threaded to every row. */
  readonly timeMode: () => TerminalTimeMode;
  /** #4401 — bumped (Ctrl/⌘+F on this focused pane) to open or re-focus its search. */
  readonly searchRequest: () => number;
  /** #4401 — the title bar's Search button: asks the caller to bump `searchRequest`. */
  readonly onOpenSearch: () => void;
  readonly canClose: () => boolean;
  readonly onFocus: () => void;
  readonly onSplitRow: () => void;
  readonly onSplitColumn: () => void;
  readonly onToggleZoom: () => void;
  readonly onToggleVerbose: () => void;
  readonly onClose: () => void;
  readonly onOpenCommandLine: () => void;
  readonly isLoading: () => boolean;
  readonly isError: () => boolean;
  readonly error: () => unknown;
  readonly hasMoreBackfill: () => boolean;
  readonly isFetchingBackfill: () => boolean;
  readonly onLoadOlder: () => void;
  readonly onRetry: () => void;
}): JSX.Element {
  const query = createMemo(() => parseTerminalFilter(props.filterText()));
  // The shared buffer holds verbose rows the moment ANY pane wants them
  // (`use-terminal-panes.ts`'s `anyVerbose` widens the ONE window-level
  // dial) — a pane whose OWN toggle is off subtracts them back out first, so
  // `kindMatched`/`filteredRows` never see a row this pane didn't ask for.
  const sinceClear = createMemo(() => {
    const marker = props.clearedAt();
    const rows = props.rows();
    return marker === null ? rows : rows.filter((r) => compareRow(r, marker) > 0);
  });
  const verboseVisible = createMemo(() => filterVerboseRows(sinceClear(), props.verbose()));
  const kindMatched = createMemo(() => {
    const kind = props.kindFilter();
    const rows = verboseVisible();
    return kind === null ? rows : rows.filter((r) => r.subject.kind === kind);
  });
  const filteredRows = createMemo(() => filterTerminalRows(kindMatched(), query()));
  const isEmpty = createMemo(() => !filteredRows().some((r) => !r.id.startsWith('sys:')));
  // Whether THIS pane narrows the shared buffer at all — the text grammar or
  // the status-line kind cell, either one. Drives the log region's aria-label.
  const isFiltered = createMemo(
    () => props.kindFilter() !== null || !isEmptyTerminalFilter(query()),
  );
  const ariaLabel = createMemo(() =>
    t('terminal.pane_log_aria_label', {
      filter: isFiltered() ? props.label() : t('terminal.pane_unfiltered'),
    }),
  );

  const atCeiling = createMemo(() =>
    atScrollbackCeiling(props.rows().length, props.hasMoreBackfill()),
  );
  const oldestTs = createMemo(() => props.rows()[0]?.ts ?? null);

  // IN-PANE SEARCH (#4401): `use-pane-search.ts` owns the state; Escape
  // hands focus back to this pane's own log.
  let paneEl: HTMLDivElement | undefined;
  const search = usePaneSearch(filteredRows, props.searchRequest);
  function closeSearch(): void {
    search.close();
    paneEl?.querySelector<HTMLElement>('.terminal__body')?.focus();
  }

  return (
    <div class="terminal__pane" ref={paneEl}>
      <TerminalPaneTitlebar
        label={props.label()}
        focused={props.focused()}
        live={props.live()}
        zoomed={props.zoomed()}
        verbose={props.verbose()}
        canClose={props.canClose()}
        onFocus={props.onFocus}
        onSplitRow={props.onSplitRow}
        onSplitColumn={props.onSplitColumn}
        onToggleZoom={props.onToggleZoom}
        onToggleVerbose={props.onToggleVerbose}
        onClose={props.onClose}
        onOpenCommandLine={props.onOpenCommandLine}
        onOpenSearch={props.onOpenSearch}
      />
      <Show when={search.open()}>
        <TerminalPaneSearch
          id={props.id}
          query={search.query()}
          onQueryChange={search.setQuery}
          current={search.current()}
          total={search.total()}
          searchedBackTo={filteredRows()[0]?.ts ?? null}
          onStep={search.step}
          onClose={closeSearch}
          focusRequest={search.focusRequest}
        />
      </Show>
      <div class="terminal__pane-filter">
        <label class="sr-only" for={`terminal-pane-filter-${props.id}`}>
          {t('terminal.pane_filter_label')}
        </label>
        <input
          id={`terminal-pane-filter-${props.id}`}
          type="text"
          class="terminal__pane-filter-input"
          placeholder={t('terminal.pane_filter_placeholder')}
          value={props.filterText()}
          onFocus={props.onFocus}
          onInput={(e) => props.onFilterTextChange(e.currentTarget.value)}
        />
      </div>
      <TerminalLog
        rows={filteredRows()}
        ariaLabel={ariaLabel()}
        isEmpty={isEmpty()}
        isLoading={props.isLoading()}
        isError={props.isError()}
        error={props.error()}
        hasMore={props.hasMoreBackfill() && !atCeiling()}
        isFetchingMore={props.isFetchingBackfill()}
        onLoadOlder={props.onLoadOlder}
        onRetry={props.onRetry}
        isLive={props.live()}
        follow={props.follow()}
        onFollowChange={props.onFollowChange}
        timeMode={props.timeMode()}
        matchIds={search.matchIds()}
        activeMatchId={search.activeMatchId()}
      />
      <Show when={atCeiling() ? oldestTs() : null}>
        {(ts) => (
          <p class="terminal__pane-scrollback-prompt">
            {t('terminal.scrollback_ceiling', {
              count: TERMINAL_BUFFER_CAPACITY,
              ts: absolute(ts()),
            })}
          </p>
        )}
      </Show>
    </div>
  );
}
